import React, { useState, useEffect } from "react";
import {
    X,
    BarChart3,
    Loader2,
    Check,
    ExternalLink,
    User,
    Briefcase,
    Trash2,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { clearAllSkillAnalysisCaches } from "../../../../api/client";

const SkillAnalysisModal = ({
    isOpen,
    onClose,
    selectedCandidate,
    selectedJDForAnalysis,
    skillAnalysis,
    skillAnalysisLoading,
    selectedSkills,
    setSelectedSkills,
    onApplySkillsAndReturn,
    reanalyzeSkills,
    analysisFromMatchingResumes,
    onClearCachesAndRestart,
}) => {
    const { t } = useTranslation();
    const [clearingCaches, setClearingCaches] = useState(false);
    const [showClearCacheConfirm, setShowClearCacheConfirm] = useState(false);

    useEffect(() => {
        if (!isOpen) {
            setShowClearCacheConfirm(false);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const candidateApiId =
        selectedCandidate?.id ?? selectedCandidate?.candidate_id;

    const handleReanalyze = async (source) => {
        if (!candidateApiId || !selectedJDForAnalysis || !reanalyzeSkills) return;
        try {
            await reanalyzeSkills(
                candidateApiId,
                selectedJDForAnalysis.id,
                source
            );
        } catch (error) {
            console.error("Error re-analyzing skills:", error);
        }
    };

    const handleConfirmClearCachesAndRestart = async () => {
        if (!onClearCachesAndRestart) return;
        setShowClearCacheConfirm(false);
        setClearingCaches(true);
        try {
            await clearAllSkillAnalysisCaches();
            await onClearCachesAndRestart();
        } catch (error) {
            console.error("Error clearing caches or restarting analysis:", error);
        } finally {
            setClearingCaches(false);
        }
    };

    const handleSkillSelection = (skill, isSelected) => {
        const jdId = selectedJDForAnalysis?.id;
        if (!jdId) return;

        setSelectedSkills((prev) => ({
            ...prev,
            [jdId]: isSelected
                ? [...(prev[jdId] || []), skill]
                : (prev[jdId] || []).filter((s) => s !== skill),
        }));
    };

    const currentSelectedSkills =
        selectedSkills[selectedJDForAnalysis?.id] || [];

    return (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[10001] p-4">
            <div className="bg-white rounded-xl w-full max-w-5xl max-h-[95vh] flex flex-col shadow-xl overflow-hidden" style={{ fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial' }}>

                {/* HEADER */}
                <div
                    className="flex justify-between items-start p-3 border-b"
                    style={{
                        background: "linear-gradient(135deg, #0ea5e9, #d946ef)",
                    }}
                >
                    <div>
                        <h2 className="text-white text-xl font-semibold mb-0.5">
                            {t("skillAnalysisModal.title")}
                        </h2>

                        <p className="text-blue-100 text-lg leading-snug">
                            {selectedCandidate?.candidate_name || `${selectedCandidate?.firstName || ""} ${selectedCandidate?.lastName || ""}`.trim() || t("common.candidate", "Candidate")} —{" "}
                            {selectedJDForAnalysis?.title}
                        </p>
                    </div>

                    <div className="flex items-center gap-1.5 shrink-0">
                        {onClearCachesAndRestart && (
                            <button
                                type="button"
                                onClick={() => setShowClearCacheConfirm(true)}
                                disabled={
                                    skillAnalysisLoading || clearingCaches
                                }
                                title={t("skillAnalysisModal.clearCachesTooltip")}
                                className="flex items-center gap-1.5 text-white text-xs font-medium px-2 py-1.5 rounded-lg border border-white/40 hover:bg-white/20 disabled:opacity-50 disabled:pointer-events-none"
                            >
                                {clearingCaches ? (
                                    <Loader2
                                        className="animate-spin shrink-0"
                                        size={14}
                                    />
                                ) : (
                                    <Trash2 size={14} className="shrink-0" />
                                )}
                                <span className="hidden sm:inline max-w-[11rem] text-left leading-tight">
                                    {t("skillAnalysisModal.clearCachesButton")}
                                </span>
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={onApplySkillsAndReturn}
                            className="text-white hover:bg-white/20 rounded-lg p-1.5"
                            aria-label={t("skillAnalysisModal.closeAria")}
                        >
                            <X size={18} />
                        </button>
                    </div>
                </div>

                {/* CONTENT */}
                <div className="flex-1 overflow-y-auto p-4 space-y-5">

                    {/* LOADING */}
                    {skillAnalysisLoading && (
                        <div className="text-center py-12">
                            <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                                <Loader2
                                    className="animate-spin text-blue-600"
                                    size={32}
                                />
                            </div>
                            <h3 className="text-base text-gray-900 mb-1">
                                {t("skillAnalysisModal.analyzingSkills")}
                            </h3>
                            <p className="text-gray-600">
                                {t("skillAnalysisModal.processingResumeJd")}
                            </p>
                        </div>
                    )}

                    {/* NO ANALYSIS */}
                    {!skillAnalysisLoading && !skillAnalysis && (
                        <div className="text-center py-12">
                            <div className="bg-gray-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                                <BarChart3 size={32} className="text-gray-400" />
                            </div>
                            <h3 className="text-base text-gray-700 mb-1">
                                {t("skillAnalysisModal.noAnalysisTitle")}
                            </h3>
                            <p className="text-gray-500">
                                {t("skillAnalysisModal.noAnalysisHint")}
                            </p>
                        </div>
                    )}

                    {/* ANALYSIS RESULTS */}
                    {!skillAnalysisLoading && skillAnalysis && (
                        <div className="space-y-5">

                            {/* WARNINGS */}
                            {skillAnalysis.warnings?.length > 0 && (
                                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                                    <h4 className="text-sm font-semibold text-amber-800 mb-2">
                                        {t("skillAnalysisModal.analysisWarnings")}
                                    </h4>

                                    <ul className="text-sm text-amber-700 ml-1 space-y-1">
                                        {skillAnalysis.warnings.map((w, i) => (
                                            <li key={i}>• {w}</li>
                                        ))}
                                    </ul>

                                    <div className="flex flex-wrap gap-2 mt-3">
                                        <button
                                            onClick={() => handleReanalyze("jd")}
                                            className="px-3 py-1.5 text-xs font-medium rounded-lg bg-amber-100 hover:bg-amber-200 text-amber-800"
                                        >
                                            {t("skillAnalysisModal.reanalyzeJD")}
                                        </button>

                                        <button
                                            onClick={() => handleReanalyze("resume")}
                                            className="px-3 py-1.5 text-xs font-medium rounded-lg bg-amber-100 hover:bg-amber-200 text-amber-800"
                                        >
                                            {t("skillAnalysisModal.reanalyzeResume")}
                                        </button>

                                        <button
                                            onClick={() => handleReanalyze("both")}
                                            className="px-3 py-1.5 text-xs font-medium rounded-lg bg-amber-100 hover:bg-amber-200 text-amber-800"
                                        >
                                            {t("skillAnalysisModal.reanalyzeAll")}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* MATCH SCORE */}
                            {!analysisFromMatchingResumes && (
                                <div className="bg-gradient-to-br from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-4">
                                    <div className="flex gap-3 items-center mb-4">
                                        <div
                                            className="p-2 rounded-full"
                                            style={{
                                                background:
                                                    "linear-gradient(135deg, #0ea5e9, #d946ef)",
                                            }}
                                        >
                                            <BarChart3
                                                size={18}
                                                className="text-white"
                                            />
                                        </div>
                                        <h3 className="text-gray-900 text-base font-semibold">
                                            {t("skillAnalysisModal.overallMatchScore")}
                                        </h3>
                                    </div>

                                    {(() => {
                                        const breakdown = skillAnalysis.semantic_match?.breakdown || skillAnalysis.breakdown || {};
                                        const summary = skillAnalysis.skill_analysis?.summary;
                                        const matched = summary?.matched_count || 0;
                                        const total = summary?.jd_skills_count || 1;
                                        const coverage = (
                                            skillAnalysis.semantic_match?.coverage !== undefined ? skillAnalysis.semantic_match.coverage :
                                            (skillAnalysis.coverage !== undefined ? skillAnalysis.coverage : (breakdown.coverage || 0))
                                        ) * 100;
                                        
                                        const percent = 
                                            skillAnalysis.semantic_match?.final_score !== undefined ? skillAnalysis.semantic_match.final_score :
                                            (skillAnalysis.final_score !== undefined ? skillAnalysis.final_score : 
                                            (skillAnalysis.match_percentage || 
                                            summary?.match_percentage ||
                                            (matched / total) * 100));

                                        const skillMatches = [
                                            ...(breakdown.required_skills || []),
                                            ...(breakdown.optional_skills || [])
                                        ].filter(s => s.status === 'matched');

                                        return (
                                            <div className="flex flex-col gap-4">
                                                <div className="flex items-center gap-5">
                                                    <div className="text-4xl text-blue-600 font-semibold">
                                                        {percent.toFixed(1)}%
                                                    </div>
                                                    <div className="flex-1">
                                                        <div className="w-full h-3 bg-gray-200 rounded-full mb-2">
                                                            <div
                                                                className="h-3 rounded-full"
                                                                style={{
                                                                    width: `${percent}%`,
                                                                    background:
                                                                        "linear-gradient(135deg, #0ea5e9, #d946ef)",
                                                                }}
                                                            />
                                                        </div>
                                                        <p className="text-sm text-gray-700">
                                                            {percent >= 80
                                                                ? t("skillAnalysisModal.matchExcellent")
                                                                : percent >= 60
                                                                ? t("skillAnalysisModal.matchGood")
                                                                : percent >= 40
                                                                ? t("skillAnalysisModal.matchFair")
                                                                : t("skillAnalysisModal.matchPoor")}
                                                        </p>
                                                    </div>
                                                </div>

                                                {/* Semantic Breakdown Section */}
                                                {skillMatches.length > 0 && (
                                                    <div className="mt-4 pt-4 border-t border-blue-100">
                                                        <h4 className="text-sm font-bold text-gray-800 mb-3 uppercase tracking-wider">Semantic Match Transparency</h4>
                                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                            {/* Coverage & Experience */}
                                                            <div className="space-y-3">
                                                                <div className="bg-white p-3 rounded-lg border border-blue-100 shadow-sm">
                                                                    <div className="flex justify-between items-center mb-1.5">
                                                                        <span className="text-xs font-medium text-gray-600">Requirement Coverage</span>
                                                                        <span className="text-xs font-bold text-blue-600">{coverage.toFixed(0)}%</span>
                                                                    </div>
                                                                    <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                                                        <div className="h-full bg-blue-500" style={{ width: `${coverage}%` }} />
                                                                    </div>
                                                                </div>
                                                                {breakdown.experience_factor !== 0 && (
                                                                    <div className="bg-emerald-50 p-3 rounded-lg border border-emerald-100 flex justify-between items-center">
                                                                        <span className="text-xs font-medium text-emerald-800">Experience Bonus</span>
                                                                        <span className="text-xs font-bold text-emerald-600">+{breakdown.experience_factor}%</span>
                                                                    </div>
                                                                )}
                                                                
                                                                {skillAnalysis.semantic_match?.candidate_experience_years !== undefined && (
                                                                    <div className="bg-blue-50 p-3 rounded-lg border border-blue-100 space-y-1.5">
                                                                        <div className="flex justify-between items-center">
                                                                            <span className="text-[10px] font-medium text-gray-600 uppercase tracking-tight">Experience Match</span>
                                                                            <span className="text-[10px] font-bold text-blue-600">
                                                                                {skillAnalysis.semantic_match.candidate_experience_years}y / {skillAnalysis.semantic_match.required_experience_years}y
                                                                            </span>
                                                                        </div>
                                                                        <div className="w-full h-1 bg-gray-200 rounded-full overflow-hidden">
                                                                            <div 
                                                                                className="h-full bg-blue-500" 
                                                                                style={{ 
                                                                                    width: `${Math.min(100, (skillAnalysis.semantic_match.candidate_experience_years / (skillAnalysis.semantic_match.required_experience_years || 1)) * 100)}%` 
                                                                                }} 
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>

                                                            {/* Skill Similarities */}
                                                            <div className="bg-white p-3 rounded-lg border border-blue-100 shadow-sm">
                                                                <span className="text-xs font-medium text-gray-600 block mb-2">Detailed Skill Similarities</span>
                                                                <div className="space-y-2 max-h-[120px] overflow-y-auto pr-1 custom-scrollbar">
                                                                    {skillMatches.map((detail, idx) => (
                                                                        <div key={idx} className="flex flex-col gap-1">
                                                                            <div className="flex items-center justify-between gap-2">
                                                                                <span className="text-[11px] font-bold text-gray-700 truncate flex-1">{detail.jd_skill}</span>
                                                                                <span className="text-[10px] font-bold text-emerald-600">{(detail.similarity * 100).toFixed(0)}%</span>
                                                                            </div>
                                                                            {detail.best_match && detail.best_match.toLowerCase() !== detail.jd_skill.toLowerCase() && (
                                                                                <span className="text-[9px] text-gray-500">matched as: {detail.best_match}</span>
                                                                            )}
                                                                            <div className="w-full h-1 bg-gray-100 rounded-full overflow-hidden">
                                                                                <div className="h-full bg-emerald-500" style={{ width: `${detail.similarity * 100}%` }} />
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })()}
                                </div>
                            )}

                            {/* === SKILL CATEGORIES === */}

                            {/* PERFECT MATCHES */}
                            {(skillAnalysis.matched_skills ||
                                skillAnalysis.skill_analysis?.matched_skills)?.length >
                                0 && (
                                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                                    <div className="px-5 py-3 bg-green-50 border-b border-green-200 flex items-center gap-3">
                                        <div className="bg-green-100 rounded-full p-2">
                                            <Check
                                                size={18}
                                                className="text-green-600"
                                            />
                                        </div>
                                        <div>
                                            <h4 className="text-green-800 font-semibold text-base text-lg">
                                                {t("skillAnalysisModal.perfectMatches")}
                                            </h4>
                                            <p className="text-green-700 text-md">
                                                {t(
                                                    "skillAnalysisModal.presentInBothResumeJd"
                                                )}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="p-4 flex flex-wrap gap-2">
                                        {(skillAnalysis.matched_skills ||
                                            skillAnalysis.skill_analysis
                                                ?.matched_skills
                                        ).map((skill, i) => {
                                            const s = skill.name || skill;
                                            const active =
                                                currentSelectedSkills.includes(
                                                    s
                                                );

                                            return (
                                                <span
                                                    key={i}
                                                    onClick={() =>
                                                        handleSkillSelection(
                                                            s,
                                                            !active
                                                        )
                                                    }
                                                    className={`px-2 py-1 text-sm rounded-md border cursor-pointer ${
                                                        active
                                                            ? "text-white border-green-600 shadow-sm"
                                                            : "bg-green-50 text-green-800 border-green-200"
                                                    }`}
                                                    style={
                                                        active
                                                            ? {
                                                                  background:
                                                                      "linear-gradient(135deg, #22c55e, #15803d)",
                                                              }
                                                            : {}
                                                    }
                                                >
                                                    {s}
                                                </span>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* REQUIRED SKILLS */}
                            {(skillAnalysis.required_skills ||
                                skillAnalysis.skill_analysis
                                    ?.jd_only_skills)?.length > 0 && (
                                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                                    <div className="px-5 py-3 bg-amber-50 border-b border-amber-200 flex items-center gap-3">
                                        <div className="bg-amber-100 p-2 rounded-full">
                                            <ExternalLink
                                                size={18}
                                                className="text-amber-600"
                                            />
                                        </div>
                                        <div>
                                            <h4 className="text-amber-800 font-semibold text-base text-lg">
                                                {t(
                                                    "skillAnalysisModal.requiredSkillsToExplore"
                                                )}
                                            </h4>
                                            <p className="text-amber-700 text-md">
                                                {t(
                                                    "skillAnalysisModal.jdSkillsMissingInResume"
                                                )}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="p-4 flex flex-wrap gap-2">
                                        {(skillAnalysis.required_skills ||
                                            skillAnalysis.skill_analysis
                                                ?.jd_only_skills
                                        ).map((skill, i) => {
                                            const s = skill.name || skill;
                                            const active =
                                                currentSelectedSkills.includes(
                                                    s
                                                );

                                            return (
                                                <span
                                                    key={i}
                                                    onClick={() =>
                                                        handleSkillSelection(
                                                            s,
                                                            !active
                                                        )
                                                    }
                                                    className={`px-2 py-1 text-sm rounded-md cursor-pointer border ${
                                                        active
                                                            ? "text-white border-amber-600 shadow-sm"
                                                            : "bg-amber-50 text-amber-800 border-amber-200"
                                                    }`}
                                                    style={
                                                        active
                                                            ? {
                                                                  background:
                                                                      "linear-gradient(135deg, #f59e0b, #d97706)",
                                                              }
                                                            : {}
                                                    }
                                                >
                                                    {s}
                                                </span>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* ADDITIONAL STRENGTHS */}
                            {(skillAnalysis.nice_to_have_skills ||
                                skillAnalysis.skill_analysis
                                    ?.resume_only_skills)?.length > 0 && (
                                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                                    <div className="px-5 py-3 bg-blue-50 border-b border-blue-200 flex items-center gap-3">
                                        <div className="bg-blue-100 p-2 rounded-full">
                                            <User
                                                size={18}
                                                className="text-blue-600"
                                            />
                                        </div>
                                        <div>
                                            <h4 className="text-blue-800 font-semibold text-base text-lg">
                                                {t(
                                                    "skillAnalysisModal.additionalStrengths"
                                                )}
                                            </h4>
                                            <p className="text-blue-700 text-sm">
                                                {t(
                                                    "skillAnalysisModal.skillsInResumeOnly"
                                                )}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="p-4 flex flex-wrap gap-2">
                                        {(skillAnalysis.nice_to_have_skills ||
                                            skillAnalysis.skill_analysis
                                                ?.resume_only_skills
                                        ).map((skill, i) => {
                                            const s = skill.name || skill;
                                            const active =
                                                currentSelectedSkills.includes(
                                                    s
                                                );

                                            return (
                                                <span
                                                    key={i}
                                                    onClick={() =>
                                                        handleSkillSelection(
                                                            s,
                                                            !active
                                                        )
                                                    }
                                                    className={`px-2 py-1 text-sm rounded-md cursor-pointer border ${
                                                        active
                                                            ? "text-white border-blue-600 shadow-sm"
                                                            : "bg-blue-50 text-blue-800 border-blue-200"
                                                    }`}
                                                    style={
                                                        active
                                                            ? {
                                                                  background:
                                                                      "linear-gradient(135deg, #3b82f6, #2563eb)",
                                                              }
                                                            : {}
                                                    }
                                                >
                                                    {s}
                                                </span>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* SELECTED SKILLS SUMMARY */}
                            {currentSelectedSkills.length > 0 && (
                                <div className="bg-purple-50 border border-purple-200 rounded-lg p-5">
                                    <div className="flex items-center gap-3 mb-3">
                                        <div
                                            className="p-2 rounded-full"
                                            style={{
                                                background:
                                                    "linear-gradient(135deg, #0ea5e9, #d946ef)",
                                            }}
                                        >
                                            <Check
                                                size={18}
                                                className="text-white"
                                            />
                                        </div>
                                        <h3 className="text-purple-900 font-semibold text-base text-lg">
                                            {t(
                                                "skillAnalysisModal.interviewFocusAreas"
                                            )}
                                        </h3>
                                    </div>

                                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2">
                                        {currentSelectedSkills.map((skill, i) => (
                                            <span
                                                key={i}
                                                className="px-2 py-1 text-sm text-white text-center rounded-md shadow-sm"
                                                style={{
                                                    background:
                                                        "linear-gradient(135deg, #0ea5e9, #d946ef)",
                                                }}
                                            >
                                                {skill}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* FOOTER */}
                <div className="border-t bg-gray-50 p-3 rounded-b-xl">
                    <div className="flex items-center justify-between">
                        <div className="bg-white px-3 py-2 rounded-md border border-gray-200 text-gray-600 text-lg">
                            {selectedJDForAnalysis ? (
                                <>
                                    <div className="flex items-center gap-2 font-medium">
                                        <Briefcase size={14} />
                                        {selectedJDForAnalysis.title}
                                    </div>
                                    <div className="text-gray-500 text-sm mt-1">
                                        {t("skillAnalysisModal.skillsSelected", {
                                            count: currentSelectedSkills.length,
                                        })}
                                    </div>
                                </>
                            ) : (
                                t("skillAnalysisModal.selectJdToAnalyze")
                            )}
                        </div>

                        <button
                            onClick={onApplySkillsAndReturn}
                            className="px-4 py-2 text-white rounded-lg text-lg hover:opacity-90 transition shadow-sm"
                            style={{
                                background:
                                    "linear-gradient(135deg, #0ea5e9, #d946ef)",
                            }}
                        >
                            {t("skillAnalysisModal.applySkillsReturn")}
                        </button>
                    </div>
                </div>
            </div>

            {showClearCacheConfirm && (
                <div
                    className="fixed inset-0 z-[10002] flex items-center justify-center p-4 bg-black/55"
                    role="presentation"
                    onClick={() =>
                        !clearingCaches && setShowClearCacheConfirm(false)
                    }
                >
                    <div
                        className="bg-white rounded-xl shadow-2xl max-w-md w-full p-5 border border-gray-200"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="clear-cache-confirm-title"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h3
                            id="clear-cache-confirm-title"
                            className="text-lg font-semibold text-gray-900"
                        >
                            {t("skillAnalysisModal.confirmClearTitle")}
                        </h3>
                        <p className="mt-3 text-sm text-gray-600 leading-relaxed">
                            {t("skillAnalysisModal.confirmClearDescription")}
                        </p>
                        <div className="mt-6 flex flex-wrap justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => setShowClearCacheConfirm(false)}
                                disabled={clearingCaches}
                                className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                            >
                                {t("skillAnalysisModal.cancel")}
                            </button>
                            <button
                                type="button"
                                onClick={handleConfirmClearCachesAndRestart}
                                disabled={clearingCaches}
                                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg text-white disabled:opacity-50"
                                style={{
                                    background:
                                        "linear-gradient(135deg, #0ea5e9, #d946ef)",
                                }}
                            >
                                {clearingCaches && (
                                    <Loader2
                                        className="animate-spin shrink-0"
                                        size={16}
                                    />
                                )}
                                {t("skillAnalysisModal.confirmClearCta")}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SkillAnalysisModal;
